import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getCancellationReport } from '../../services/api';
import {
  escapeHtml as html,
  loadReportCompany,
  reportBrandHtml,
  REPORT_BRAND_CSS,
} from '../../utils/reportBranding';
import PageHeader from '../common/PageHeader';
import ResponsiveTable from '../common/ResponsiveTable';
import SortableTh from '../common/SortableTh';
import { useClientSort } from '../../hooks/useClientSort';
import { toDateInputValue } from '../../utils/datetimeLocal';
import { useEstabelecimentoFiltroApi } from '../../context/EstabelecimentoContext';

const currency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const localDate = toDateInputValue;

export default function CancellationsReport() {
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const [filters, setFilters] = useState({ de: localDate(first), ate: localDate(now) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getCancellationReport({ ...filters, estabelecimentoId });
      setData(response.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Não foi possível carregar os cancelamentos.');
    } finally {
      setLoading(false);
    }
  }, [filters, estabelecimentoId]);

  useEffect(() => {
    load();
  }, [load]);

  const cancellationAccessors = useMemo(() => ({
    venda: (x) => x.dataVenda,
    cancelamento: (x) => x.dataCancelamento,
    pdvOperador: (x) => `${x.pdv || ''} ${x.usuario || ''}`,
    valor: (x) => Number(x.valor) || 0,
  }), []);
  const { sorted: cancelamentosOrdenados, orderBy, order, requestSort } = useClientSort(
    data?.items || [],
    'dataCancelamento',
    'desc',
    cancellationAccessors,
  );

  const exportPdf = async () => {
    const popup = window.open('', '_blank', 'width=1100,height=760');
    if (!popup) {
      setError('Libere pop-ups para gerar o PDF.');
      return;
    }
    try {
      const company = await loadReportCompany();
      const rows = (data?.items || [])
        .map(
          (x) => `<tr>
            <td>#${html(x.id)}</td>
            <td>${html(new Date(x.dataCancelamento || x.dataVenda).toLocaleString('pt-BR'))}</td>
            <td>${html(x.pdv)}<br/><small>${html(x.usuario)}</small></td>
            <td>${html(x.cliente)}</td>
            <td>${html(x.motivo || '-')}</td>
            <td style="text-align:right">${html(currency(x.valor))}</td>
          </tr>`
        )
        .join('');
      popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Cancelamentos</title>
        <style>
          body{font:11px Arial;padding:18px;color:#172033}
          h1{margin:4px 0 0}
          ${REPORT_BRAND_CSS}
          table{width:100%;border-collapse:collapse;margin-top:12px}
          th,td{border-bottom:1px solid #e2e6ed;padding:6px;text-align:left;vertical-align:top}
          th{background:#eaf6fe}
        </style></head><body>
        ${reportBrandHtml(company, { title: 'Relatório de cancelamentos' })}
        <div style="margin-top:8px">Período: ${html(filters.de)} a ${html(filters.ate)}</div>
        <div>Total: ${html(data?.quantidade || 0)} · ${html(currency(data?.valorTotal))}</div>
        <table><thead><tr><th>Venda</th><th>Cancelamento</th><th>PDV/Operador</th><th>Cliente</th><th>Motivo</th><th>Valor</th></tr></thead>
        <tbody>${rows}</tbody></table></body></html>`);
      popup.document.close();
      setTimeout(() => popup.print(), 300);
    } catch (e) {
      popup.close();
      setError(e?.response?.data?.error || 'Falha ao gerar PDF.');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Cancelamentos"
        subtitle="Vendas canceladas no período, com motivo e operador."
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField
            label="De"
            type="date"
            size="small"
            value={filters.de}
            onChange={(e) => setFilters((p) => ({ ...p, de: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            value={filters.ate}
            onChange={(e) => setFilters((p) => ({ ...p, ate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={load} disabled={loading}>
            Gerar
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            disabled={!data}
            onClick={exportPdf}
          >
            PDF
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Card variant="outlined"><CardContent>
            <Typography variant="caption" color="text.secondary">Canceladas</Typography>
            <Typography variant="h5" fontWeight={900}>{data?.quantidade || 0}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined"><CardContent>
            <Typography variant="caption" color="text.secondary">Valor total</Typography>
            <Typography variant="h5" fontWeight={900} color="error.main">
              {currency(data?.valorTotal)}
            </Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="id" label="Venda" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cancelamento" label="Cancelamento" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="pdvOperador" label="PDV/Operador" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cliente" label="Cliente" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="motivo" label="Motivo" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="valor" label="Valor" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {cancelamentosOrdenados.map((x) => (
              <TableRow key={x.id}>
                <TableCell data-label="Venda">#{x.id}<br /><small>{new Date(x.dataVenda).toLocaleString('pt-BR')}</small></TableCell>
                <TableCell data-label="Cancelamento">
                  {x.dataCancelamento
                    ? new Date(x.dataCancelamento).toLocaleString('pt-BR')
                    : '-'}
                </TableCell>
                <TableCell data-label="PDV/operador">{x.pdv}<br /><small>{x.usuario}</small></TableCell>
                <TableCell data-label="Cliente">{x.cliente}</TableCell>
                <TableCell data-label="Motivo">{x.motivo || <Chip size="small" label="Sem motivo" />}</TableCell>
                <TableCell align="right" data-label="Valor"><strong>{currency(x.valor)}</strong></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
    </Box>
  );
}
